import { useCallback, useState, useRef } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { Check, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import {
  type TaskFormData,
  useGetTask,
  useUpdateTask,
  type TaskFilterView,
} from '@zendia/frontend-shared'
import { axiosRequest } from '../../../../utilities/axiosClient'
import { TaskForm, type TaskFormRef } from '../../../../components/Task'
import {
  parseTasksPanelTab,
  type TasksPanelTab,
} from '../../../../components/dashboard/tasksPanelTab'

export const Route = createFileRoute('/_authenticated/tasks/$taskId/edit')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
  validateSearch: (
    search,
  ): {
    view: TaskFilterView
    dashboardTab?: TasksPanelTab
    fromAllTasks?: boolean
    goalId?: string
    milestoneId?: string
  } => ({
    view: search.view as TaskFilterView,
    // Which dashboard Tasks panel group the user was on before editing this task, so
    // saving (or cancelling) returns the dashboard to that same group.
    dashboardTab: parseTasksPanelTab(search.dashboardTab),
    // Set when the task was opened from the All Tasks page, so leaving returns there
    fromAllTasks: search.fromAllTasks === true || undefined,
    // Set when the task was opened from a goal's Your Plan, so leaving returns there, on the
    // same milestone
    goalId: search.goalId as string | undefined,
    milestoneId: search.milestoneId as string | undefined,
  }),
})

function RouteComponent() {
  const router = useRouter()
  const { taskId } = Route.useParams()
  const { mutateAsync: updateTask } = useUpdateTask(axiosRequest)
  const {
    data: task,
    isLoading: isTaskLoading,
    isPlaceholderData: isTaskPlaceholder,
  } = useGetTask(axiosRequest, taskId)
  const { dashboardTab, fromAllTasks, goalId, milestoneId } = Route.useSearch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const taskFormRef = useRef<TaskFormRef>(null)

  // Reached from the dashboard's Tasks panel, so leaving returns there rather
  // than to the standalone Tasks page, restoring the group the user came from
  // when one was passed along. Reached from a goal's Your Plan or the All Tasks
  // page, it returns there.
  const leave = useCallback(() => {
    if (fromAllTasks) {
      router.navigate({ to: '/tasks', search: { dashboardTab }, replace: true })
    } else if (goalId) {
      router.navigate({
        to: '/goals/$goalId',
        params: { goalId },
        search: { tab: 'plan', planMilestone: milestoneId },
        replace: true,
      })
    } else {
      router.navigate({ to: '/dashboard', search: { tab: dashboardTab }, replace: true })
    }
  }, [router, dashboardTab, fromAllTasks, goalId, milestoneId])

  const onBack = useCallback(() => {
    if (isSubmitting) return
    leave()
  }, [isSubmitting, leave])

  const onSave = useCallback(
    async (data: TaskFormData) => {
      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        await updateTask({
          taskId,
          data,
        })
        leave()
      } catch (error) {
        console.error(error)
        setSubmitError('Failed to update task')
      } finally {
        setIsSubmitting(false)
      }
    },
    [updateTask, taskId, leave],
  )

  // The form only reads its values when it mounts, so it waits for the task as the server has it -
  // never a list's copy, which could be older
  if (isTaskLoading || isTaskPlaceholder) {
    return (
      <FullView
        leftAction={
          <ActionButton
            onClick={onBack}
            label="Back"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        centerContent={<div>Edit Task</div>}
      >
        <FullViewContent>
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500">Loading task...</p>
          </div>
        </FullViewContent>
      </FullView>
    )
  }

  if (!task) {
    return (
      <FullView
        leftAction={
          <ActionButton
            onClick={onBack}
            label="Back"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        centerContent={<div>Edit Task</div>}
      >
        <FullViewContent>
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500">Task not found</p>
          </div>
        </FullViewContent>
      </FullView>
    )
  }

  return (
    <FullView
      leftAction={
        <ActionButton
          onClick={onBack}
          label="Cancel"
          icon={<ChevronBackward className="size-6" />}
        />
      }
      centerContent={<div>Edit Task</div>}
      rightAction={
        <ActionButton
          primary
          onClick={() => {
            taskFormRef.current?.submit()
          }}
          label="Save changes"
          disabled={isSubmitting}
          icon={<Check className="m-auto size-6" />}
        />
      }
    >
      <FullViewContent>
        <div className="h-full">
          <TaskForm
            ref={taskFormRef}
            initialValues={task}
            onSubmit={onSave}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        </div>
      </FullViewContent>
    </FullView>
  )
}
